import { createAsyncThunk } from "@reduxjs/toolkit";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { IAIAssistantService } from "services/AIAssistantService";
import {
  ChatReplyModel,
  ChatSessionDetailModel,
  ChatSessionModel,
  IMessageFeedbackModel,
  UploadedChatDocumentModel,
} from "services/api/cplapi/CPLAPI";
import type { Conversation } from "app/modules/aiAssistant/AIAssistantSlice";

const aiService = () => container.get<IAIAssistantService>(SERVICE_TYPES.AIAssistantService);

export const getChatSessions = createAsyncThunk<ChatSessionModel[], void>("AIAssistant/GetChatSessions", () =>
  aiService().getChatSessions()
);

export const loadChatSession = createAsyncThunk<ChatSessionDetailModel, string>(
  "AIAssistant/LoadChatSession",
  (chatSessionID) => aiService().loadChatSession(chatSessionID)
);

export const deleteChatSession = createAsyncThunk<void, string>("AIAssistant/DeleteChatSession", (chatSessionID) =>
  aiService().deleteChatSession(chatSessionID)
);

export const renameChatSession = createAsyncThunk<void, { chatSessionID: string; newChatName: string }>(
  "AIAssistant/RenameChatSession",
  ({ chatSessionID, newChatName }) => aiService().renameChatSession(chatSessionID, newChatName)
);

export const sendChatMessage = createAsyncThunk<ChatReplyModel, { conversation: Conversation; message: string }>(
  "AIAssistant/SendChatMessage",
  async ({ conversation, message }, { dispatch, signal, requestId }) => {
    const reply = await aiService().sendChatMessage(conversation.chatSessionID, message, requestId, signal);

    // Show the new chat in the chat list, with the name the API gave it
    if (conversation.chatSessionID === null) dispatch(getChatSessions());

    return reply;
  },
  // The API matches step updates to this run by its ID
  { idGenerator: () => crypto.randomUUID() }
);

export const uploadFile = createAsyncThunk<UploadedChatDocumentModel, { conversation: Conversation; file: File }>(
  "AIAssistant/UploadFile",
  async ({ conversation, file }, { dispatch }) => {
    const uploaded = await aiService().uploadFile(conversation.chatSessionID, file);

    // Show the new chat in the chat list
    if (conversation.chatSessionID === null) dispatch(getChatSessions());

    return uploaded;
  }
);

export const deleteChatDocument = createAsyncThunk<void, string>("AIAssistant/DeleteChatDocument", (documentID) =>
  aiService().deleteChatDocument(documentID)
);

export const setMessageFeedback = createAsyncThunk<void, { messageID: number; feedback: IMessageFeedbackModel }>(
  "AIAssistant/SetMessageFeedback",
  ({ messageID, feedback }) => aiService().setMessageFeedback(messageID, feedback)
);

export const removeMessageFeedback = createAsyncThunk<void, number>("AIAssistant/RemoveMessageFeedback", (messageID) =>
  aiService().deleteMessageFeedback(messageID)
);
